import { HvlEnumGridColumn, HvlFormConstants, HvlMlAbstractOperationalForm } from '@hvlng/framework-core/form';
import { Directive, Injector } from '@angular/core';
import { logComponent } from '../hr-logger';
import { HrFormButtonEvents } from './hr-form-button-events';
import { HrFormToolbarEvents } from './hr-form-toolbar-events';
import { getFormTitleAsync, getFormTitleSync, getPageCode, translateAsync } from '../page-code/page-code-func';
import { PageCode } from '../page-code/page-code';
import { PageCodeModel } from '../page-code/page-code-model';
import { HrFormLoadingVisibility } from './hr-form-loading-visibility';
import { HrBaseParam } from './hr-base-param';
import { TranslateService } from '@ngx-translate/core';
import { UiConfirmationDialogService, UiToastMessageService } from 'ui-shared';
import { ServiceCall } from '../service/service-call';
import { HvlLanguageService } from '@hvlng/framework-core/language';
import { HvlConfigurationService } from '@hvlng/framework-bff/core';

import { Router } from '@angular/router';
import { HrResourceName } from '../model/hr-resource-name';
import { PrimeNGConfig } from 'primeng/api';
import { HrResourceLoader } from '../service/hr-resource-loader';
import { HrTranslateKey } from '../translate/hr-translate-key';
import { KovanMlAbstractOperationalForm } from 'ui-shared';

@Directive()
export abstract class HrBaseMultiLangFormPage<T>
    extends KovanMlAbstractOperationalForm<T>
    implements HrFormToolbarEvents, HrFormLoadingVisibility, HrFormButtonEvents {
    private _pageCode: string;
    private _pageTitle: string;
    private _pageCodeModel: PageCodeModel;

    private resourceLoader: HrResourceLoader;
    protected translateService: TranslateService;
    public toastMessage: UiToastMessageService;
    public serviceCall: ServiceCall;
    public confirmationDialogService: UiConfirmationDialogService;
    public config: PrimeNGConfig;
    public langService: HvlLanguageService;
    public router?: Router;

    protected constructor(
        configurationService: HvlConfigurationService,
        cType: new () => T,
        pageCode: PageCode,
        injector: Injector,
        params?: HrBaseParam,
    ) {
        super(cType, 'definition', configurationService, 'detailMap');

        logComponent(this);
        this.initVariables(pageCode, injector, params);
        this.initResources();
    }

    private initVariables(pageCode: PageCode, injector: Injector, params?: HrBaseParam) {
        this._pageCodeModel = new PageCodeModel(pageCode);
        this.translateService = injector.get(TranslateService);
        this.toastMessage = injector.get(UiToastMessageService);
        this.serviceCall = injector.get(ServiceCall);
        this.confirmationDialogService = injector.get(UiConfirmationDialogService);
        this.langService = injector.get(HvlLanguageService);
        this.config = injector.get(PrimeNGConfig);
        this.resourceLoader = injector.get(HrResourceLoader);
        if (params?.hasRouter) {
            this.router = injector.get(Router);
        }
    }

    private initResources() {
        this.resourceLoader.init(this.getResourceList());
    }

    public abstract getResourceList(): Array<HrResourceName>;

    public abstract openAddForm();

    public abstract openCopyForm();

    public abstract openEditForm();

    public abstract openRemoveForm();

    public abstract openViewForm();

    public abstract onCancel();

    public abstract onSave();

    public abstract onRemove();

    public openForm(
        formMode: HvlFormConstants,
        formTitle: string,
        formColSize: HvlEnumGridColumn = HvlEnumGridColumn.COL12,
    ): void {
        this.setPageCode(formMode);
        super.openForm(formMode, formTitle, formColSize);
    }

    private setPageCode(formMode: HvlFormConstants) {
        this._pageCode = getPageCode(formMode, this.pageCodeModel);
    }

    get pageCode(): string {
        return this._pageCode;
    }

    get pageTitle(): string {
        return this._pageTitle;
    }

    public setPageTitle(title: string) {
        this._pageTitle = title;
    }

    get pageCodeModel(): PageCodeModel {
        return this._pageCodeModel;
    }

    public hideLoading() {}

    public showLoading() {}

    public get formModeEnum(): typeof HvlFormConstants {
        return HvlFormConstants;
    }

    public get hrKey(): typeof HrTranslateKey {
        return HrTranslateKey;
    }

    public translateSync(key: string): string | any {
        return this.translateService.instant(key);
    }

    public translateAsync(key: string): Promise<string | any> {
        return translateAsync(this.translateService, key);
    }

    public async getFormTitleAsyncAsSearch(pageTitle: string): Promise<string | any> {
        return getFormTitleAsync(this.translateService, pageTitle, null);
    }

    public async getFormTitleAsync(pageTitle: string, formModeParam?: HvlFormConstants): Promise<string | any> {
        return getFormTitleAsync(
            this.translateService,
            pageTitle,
            formModeParam != null ? formModeParam : this.formMode,
        );
    }

    public getFormTitleSyncAsSearch(pageTitle: string): string | any {
        return getFormTitleSync(this.translateService, pageTitle, null);
    }

    public getFormTitleSync(pageTitle: string, formModeParam?: HvlFormConstants): string | any {
        return getFormTitleSync(
            this.translateService,
            pageTitle,
            formModeParam != null ? formModeParam : this.formMode,
        );
    }

    public closeForm(): void {
        super.closeForm();
        this.formMode = HvlFormConstants.LIST;
    }
}
